/**
 * @fileoverview Organisation Onboarding Step
 *
 * Purpose: Prompts the user to create an organisation during onboarding.
 * Also triggers Stripe customer creation if not already done.
 * URL: /onboarding/organization
 * Protection: Requires authentication
 *
 * Flow:
 * - Ensures a Stripe customer is created for the user (completeOnboarding)
 * - If the user already belongs to an organisation (e.g. via invitation), skip to dashboard
 * - If the user has been invited to an existing organisation (MOR-1139),
 *   show the invitation panel instead of the create form — a pending invite
 *   can be accepted in place, an expired one names the firm to chase
 * - Otherwise, show a form to create a new organisation
 * - After creation, navigate to the dashboard
 *
 * Dependencies:
 * - Convex: organizations.queries.getUserOrganizations, organizations.mutations.createOrganization
 * - Convex: organizations.mutations.getOnboardingInvitation
 * - Convex: users.queries.getCurrentUser, users.mutations.completeOnboarding
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2, Building2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, Input, Button } from "@/ui/primitives";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useAction } from "convex/react";
import { api } from "~/convex/_generated/api";
import { Route as DashboardRoute } from "@/routes/_app/_auth/dashboard/_layout.index";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { getLocaleCurrency } from "@/utils/misc";
import { OnboardingInvitationPanel } from "./-ui.onboarding-invitation";
import { getOnboardingErrorMessage } from "./-onboarding.helpers";

export const Route = createFileRoute(
  "/_app/_auth/onboarding/_layout/organization",
)({
  component: OnboardingOrganization,
  beforeLoad: () => ({
    title: "Organisation",
  }),
});

const orgNameValidator = z
  .string()
  .min(2, "Organisation name must be at least 2 characters.")
  .max(100, "Organisation name must be less than 100 characters.");

function OnboardingOrganization() {
  const { data: user } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const { data: userOrgs, isLoading } = useQuery(
    convexQuery(api.organizations.queries.getUserOrganizations, {}),
  );
  const { mutateAsync: completeOnboarding } = useMutation({
    mutationFn: useConvexMutation(api.users.mutations.completeOnboarding),
  });
  const createOrganization = useAction(
    api.organizations.mutations.createOrganization,
  );
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const onboardingTriggered = useRef(false);

  const hasNoOrg =
    !isLoading && userOrgs !== undefined && userOrgs.length === 0;

  // Only users with no organisation need the invitation check; it hits the
  // WorkOS API, so it is not a reactive Convex query. No retries: a failure
  // just falls through to the create form, which the backend guards anyway.
  const getOnboardingInvitation = useAction(
    api.organizations.mutations.getOnboardingInvitation,
  );
  const { data: invitation, isLoading: isInvitationLoading } = useQuery({
    queryKey: ["onboardingInvitation", user?._id],
    queryFn: () => getOnboardingInvitation({}),
    enabled: hasNoOrg && !!user,
    retry: false,
  });

  // Trigger Stripe customer creation on mount if not already done
  useEffect(() => {
    if (user && !user.customerId && !onboardingTriggered.current) {
      onboardingTriggered.current = true;
      completeOnboarding({ currency: getLocaleCurrency() });
    }
  }, [user, completeOnboarding]);

  // If user already has an org (e.g. from invitation), skip to dashboard
  useEffect(() => {
    if (!isLoading && userOrgs && userOrgs.length > 0) {
      navigate({ to: DashboardRoute.fullPath });
    }
  }, [isLoading, userOrgs, navigate]);

  const form = useForm({
    defaultValues: {
      orgName: "",
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
        await createOrganization({ name: value.orgName });
        navigate({ to: DashboardRoute.fullPath });
      } catch (error) {
        console.error("Failed to create organisation:", error);
        setSubmitError(
          getOnboardingErrorMessage(
            error,
            "We couldn't create the organisation. Please try again.",
          ),
        );
        setIsSubmitting(false);
      }
    },
  });

  if (isLoading || (hasNoOrg && isInvitationLoading)) {
    return (
      <div className="mx-auto flex h-full w-full max-w-96 flex-col items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary/60" />
      </div>
    );
  }

  if (invitation) {
    return (
      <OnboardingInvitationPanel
        invitation={invitation}
        onAccepted={() => navigate({ to: DashboardRoute.fullPath })}
      />
    );
  }

  return (
    <div className="mx-auto flex h-full w-full max-w-96 flex-col items-center justify-center gap-6">
      <div className="flex flex-col items-center gap-2">
        <span className="mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <Building2 className="h-8 w-8 text-primary" />
        </span>
        <h3 className="text-center text-2xl font-medium text-primary">
          Create Your Organisation
        </h3>
        <p className="text-center text-base font-normal text-primary/60">
          Set up your organisation to start collaborating with your team.
        </p>
      </div>

      <form
        className="flex w-full flex-col items-start gap-1"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className="flex w-full flex-col gap-1.5">
          <label htmlFor="orgName" className="sr-only">
            Organisation Name
          </label>
          <form.Field
            name="orgName"
            validators={{
              onSubmit: orgNameValidator,
            }}
            children={(field) => (
              <Input
                placeholder="Organisation name"
                autoComplete="off"
                required
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                className={`bg-transparent ${
                  field.state.meta?.errors.length > 0 &&
                  "border-destructive focus-visible:ring-destructive"
                }`}
              />
            )}
          />
        </div>

        <div className="flex flex-col">
          {(form.state.fieldMeta.orgName?.errors.length ?? 0) > 0 && (
            <span className="mb-2 text-sm text-destructive dark:text-destructive-foreground">
              {form.state.fieldMeta.orgName?.errors
                .map((e) => (typeof e === "string" ? e : e?.message))
                .filter(Boolean)
                .join(" ")}
            </span>
          )}
        </div>

        {submitError && (
          <Alert variant="destructive" className="mb-2">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        )}

        <Button type="submit" size="sm" className="w-full">
          {isSubmitting ? (
            <Loader2 className="animate-spin" />
          ) : (
            "Create Organisation"
          )}
        </Button>
      </form>

      <p className="px-6 text-center text-sm font-normal leading-normal text-primary/60">
        You can invite team members and manage your organisation from the
        dashboard settings.
      </p>
    </div>
  );
}
